import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from '../../api/api';
import apiEndpoints from '../../utils/api/endpoints';

interface KozelBattleTState {
  loading: boolean;
  error: string | null;
  uuid: string | null;
  userLightScore: number;
  userDarkScore: number;
  bestLightScore: number;
  bestDarkScore: number;
}

const initialState: KozelBattleTState = {
  loading: false,
  error: null,
  uuid: localStorage.getItem('kozelBattleTUuid'),
  userLightScore: 0,
  userDarkScore: 0,
  bestLightScore: 0,
  bestDarkScore: 0,
};

export const generateKozelTUserId = createAsyncThunk(
  'kozelBattleT/generateUserId',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post(apiEndpoints.kozelBattleTGenerateId);
      if (response.data?.success && response.data?.unique_id) {
        return response.data.unique_id as string;
      }
      return rejectWithValue('Failed to generate user ID');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Could not generate user ID');
    }
  }
);

export const addKozelTScore = createAsyncThunk(
  'kozelBattleT/addScore',
  async (
    params: { unique_id: string; team: 'light' | 'dark'; score: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiInstance.post(apiEndpoints.kozelBattleTAddScore, params);
      if (response.data?.success) {
        return response.data as { light_score: number; dark_score: number };
      }
      return rejectWithValue(response.data?.message || 'Failed to add score');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Could not save score');
    }
  }
);

export const fetchKozelTBestScores = createAsyncThunk(
  'kozelBattleT/fetchBestScores',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(apiEndpoints.kozelBattleTBestScores);
      if (response.data?.success) {
        return {
          bestLightScore: Number(response.data.best_light_score) || 0,
          bestDarkScore: Number(response.data.best_dark_score) || 0,
        };
      }
      return rejectWithValue('Failed to fetch best scores');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Could not fetch best scores');
    }
  }
);

export const fetchKozelTUserScores = createAsyncThunk(
  'kozelBattleT/fetchUserScores',
  async (unique_id: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(
        `${apiEndpoints.kozelBattleTUserScores}/${unique_id}`
      );
      if (response.data?.success) {
        return {
          lightScore: Number(response.data.light_score) || 0,
          darkScore: Number(response.data.dark_score) || 0,
        };
      }
      return rejectWithValue('Player not found');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Could not fetch user scores');
    }
  }
);

const kozelBattleTSlice = createSlice({
  name: 'kozelBattleT',
  initialState,
  reducers: {
    clearKozelTUuid: (state) => {
      state.uuid = null;
      state.userLightScore = 0;
      state.userDarkScore = 0;
      localStorage.removeItem('kozelBattleTUuid');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateKozelTUserId.fulfilled, (state, action) => {
        state.uuid = action.payload;
        localStorage.setItem('kozelBattleTUuid', action.payload);
      })
      .addCase(addKozelTScore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addKozelTScore.fulfilled, (state, action) => {
        state.loading = false;
        state.userLightScore = action.payload.light_score;
        state.userDarkScore = action.payload.dark_score;
        if (action.payload.light_score > state.bestLightScore) {
          state.bestLightScore = action.payload.light_score;
        }
        if (action.payload.dark_score > state.bestDarkScore) {
          state.bestDarkScore = action.payload.dark_score;
        }
      })
      .addCase(addKozelTScore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchKozelTUserScores.fulfilled, (state, action) => {
        state.userLightScore = action.payload.lightScore;
        state.userDarkScore = action.payload.darkScore;
      })
      .addCase(fetchKozelTBestScores.fulfilled, (state, action) => {
        state.bestLightScore = action.payload.bestLightScore;
        state.bestDarkScore = action.payload.bestDarkScore;
      });
  },
});

export const { clearKozelTUuid } = kozelBattleTSlice.actions;
export default kozelBattleTSlice.reducer;